import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Shield } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";

function ObfuscationConfigPanel({ entityFields = [], userFields = [], config = {}, onChange, isEnterprise, isEditing = false }) {
    const { t } = useTranslation();
    const fields = config.fields || {};
    const rawAccessRules = config.rawAccessRules || [];
    const disabled = !isEnterprise || !isEditing;

    const updateConfig = useCallback((next) => onChange({ ...config, ...next }), [config, onChange]);

    const addField = useCallback((fieldName) => {
        if (!fieldName || fields[fieldName]) return;
        updateConfig({ fields: { ...fields, [fieldName]: { type: "full" } } });
    }, [fields, updateConfig]);

    const removeField = useCallback((fieldName) => {
        const next = { ...fields };
        delete next[fieldName];
        updateConfig({ fields: next });
    }, [fields, updateConfig]);

    const updateField = useCallback((fieldName, updates) => {
        updateConfig({ fields: { ...fields, [fieldName]: { ...fields[fieldName], ...updates } } });
    }, [fields, updateConfig]);

    const addRule = useCallback(() => {
        updateConfig({ rawAccessRules: [...rawAccessRules, { type: "userField", config: { userField: "role", matchType: "value", matchValue: "admin" } }] });
    }, [rawAccessRules, updateConfig]);

    const removeRule = useCallback((i) => {
        updateConfig({ rawAccessRules: rawAccessRules.filter((_, idx) => idx !== i) });
    }, [rawAccessRules, updateConfig]);

    const updateRule = useCallback((i, ruleConfig) => {
        const next = [...rawAccessRules];
        next[i] = { ...next[i], config: { ...next[i].config, ...ruleConfig } };
        updateConfig({ rawAccessRules: next });
    }, [rawAccessRules, updateConfig]);

    return (
        <div className="space-y-4">
            {!isEnterprise && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Shield className="w-4 h-4 text-amber-600 shrink-0" />
                    <p className="text-xs text-amber-700">{t("dataSecurity.obfuscation.enterpriseOnly")}</p>
                    <Badge variant="outline" className="ml-auto text-[10px] border-amber-300 text-amber-600">{t("dataSecurity.obfuscation.enterpriseBadge")}</Badge>
                </div>
            )}
            <div>
                <Label className="text-xs font-medium mb-2 block">{t("dataSecurity.obfuscation.maskedFields")}</Label>
                {!isEditing && Object.keys(fields).length === 0 && (
                    <p className="text-xs text-gray-400 italic">{t("dataSecurity.obfuscation.noFieldsConfigured")}</p>
                )}
                {Object.entries(fields).map(([name, cfg]) => (
                    <div key={name} className="flex items-center gap-2 mb-2 p-2 border rounded bg-white">
                        <span className="text-xs font-mono flex-1 truncate">{name}</span>
                        <Select disabled={disabled} value={cfg.type} onValueChange={(v) => updateField(name, { type: v })}>
                            <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="full">{t("dataSecurity.obfuscation.typeFull")}</SelectItem>
                                <SelectItem value="partial">{t("dataSecurity.obfuscation.typePartial")}</SelectItem>
                            </SelectContent>
                        </Select>
                        {cfg.type === "partial" && (
                            <>
                                <Input disabled={disabled} type="number" min={0} max={10} className="w-14 h-7 text-xs" placeholder="First" value={cfg.showFirst ?? 1} onChange={(e) => updateField(name, { showFirst: parseInt(e.target.value) || 0 })} />
                                <Input disabled={disabled} type="number" min={0} max={10} className="w-14 h-7 text-xs" placeholder="Last" value={cfg.showLast ?? 0} onChange={(e) => updateField(name, { showLast: parseInt(e.target.value) || 0 })} />
                            </>
                        )}
                        {isEditing && <Button disabled={disabled} variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeField(name)}><X className="w-3 h-3" /></Button>}
                    </div>
                ))}
                {isEditing && entityFields.filter((f) => !fields[f]).length > 0 && (
                    <Select disabled={disabled} onValueChange={addField}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={t("dataSecurity.obfuscation.addFieldPlaceholder")} /></SelectTrigger>
                        <SelectContent>{entityFields.filter((f) => !fields[f]).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                )}
            </div>
            <div>
                <Label className="text-xs font-medium mb-2 block">{t("dataSecurity.obfuscation.rawAccessLabel")}</Label>
                {!isEditing && rawAccessRules.length === 0 && (
                    <p className="text-xs text-gray-400 italic">{t("dataSecurity.obfuscation.noRulesConfigured")}</p>
                )}
                {rawAccessRules.map((rule, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2 p-2 border rounded bg-white">
                        <Select disabled={disabled} value={rule.config?.userField || "role"} onValueChange={(v) => updateRule(i, { userField: v })}>
                            <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{userFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-xs text-gray-400">=</span>
                        <Input disabled={disabled} className="flex-1 h-7 text-xs" value={rule.config?.matchValue || ""} onChange={(e) => updateRule(i, { matchValue: e.target.value })} />
                        {isEditing && <Button disabled={disabled} variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeRule(i)}><X className="w-3 h-3" /></Button>}
                    </div>
                ))}
                {isEditing && (
                    <Button disabled={disabled} variant="outline" size="sm" className="text-xs h-7" onClick={addRule}><Plus className="w-3 h-3 mr-1" />{t("dataSecurity.obfuscation.addRule")}</Button>
                )}
            </div>
        </div>
    );
}

export default ObfuscationConfigPanel;
